'use client';

import type { MysteryRoomDoorRuntimeState, MysteryRoomHeroState, MysteryRoomInventoryItemState } from '../core/localRuntime';
import type { MysteryRoomAccessLevel } from '../world/mapTypes';

export type DoorActor = {
  actorId: string;
  actorType?: 'hero' | 'npc' | 'player' | 'bot';
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  accessLevels: MysteryRoomAccessLevel[];
  inventoryItemIds?: string[];
};

const DOOR_OPEN_MS = 220;
const DOOR_CLOSE_MS = 260;
const DOOR_HOLD_OPEN_MS = 1100;

export function updateDoors(
  doors: MysteryRoomDoorRuntimeState[],
  actors: DoorActor[],
  dtMs: number,
): MysteryRoomDoorRuntimeState[] {
  return doors.map((door) => {
    const overlappingActors = actors.filter((actor) => rectsOverlap(actor.bounds, door.trigger));
    const actorWithAccess = overlappingActors.find((actor) => canDoorActorUseDoor(door, actor));

    // Зачем: только герой без пропуска «ломает» дверь; NPC в зоне не должны отменять opening после P2P-toggle хоста.
    const unauthorizedHeroOverlap = overlappingActors.some(
      (actor) =>
        (actor.actorType === 'hero' || actor.actorType === 'player') && !canDoorActorUseDoor(door, actor),
    );
    // Зачем: герой хоста без пропуска не отменяет уже идущее/завершённое открытие от гостя (P2P).
    if (
      !actorWithAccess &&
      unauthorizedHeroOverlap &&
      door.state !== 'opening' &&
      door.state !== 'open' &&
      door.state !== 'closing'
    ) {
      return {
        ...door,
        state: 'locked',
        openProgress: 0,
        holdOpenMs: 0,
      };
    }

    if (actorWithAccess) {
      const nextProgress = Math.min(1, door.openProgress + dtMs / DOOR_OPEN_MS);
      return {
        ...door,
        state: nextProgress >= 1 ? 'open' : 'opening',
        openProgress: nextProgress,
        holdOpenMs: DOOR_HOLD_OPEN_MS,
      };
    }

    if (door.state === 'locked') {
      return {
        ...door,
        state: 'closed',
      };
    }

    if (door.state === 'open') {
      const nextHoldOpenMs = Math.max(0, door.holdOpenMs - dtMs);
      if (nextHoldOpenMs > 0) {
        return {
          ...door,
          holdOpenMs: nextHoldOpenMs,
        };
      }

      return {
        ...door,
        state: 'closing',
        holdOpenMs: 0,
      };
    }

    if (door.state === 'closing') {
      const nextProgress = Math.max(0, door.openProgress - dtMs / DOOR_CLOSE_MS);
      return {
        ...door,
        state: nextProgress <= 0 ? 'closed' : 'closing',
        openProgress: nextProgress,
      };
    }

    if (door.state === 'opening') {
      const nextProgress = Math.min(1, door.openProgress + dtMs / DOOR_OPEN_MS);
      return {
        ...door,
        state: nextProgress >= 1 ? 'open' : 'opening',
        openProgress: nextProgress,
        holdOpenMs: DOOR_HOLD_OPEN_MS,
      };
    }

    return door;
  });
}

export function canDoorActorUseDoor(door: MysteryRoomDoorRuntimeState, actor: Pick<DoorActor, 'actorType' | 'accessLevels' | 'inventoryItemIds'>): boolean {
  const accessMode = door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required');
  if (accessMode === 'public') {
    return true;
  }
  if (accessMode === 'npc_only') {
    return actor.actorType === 'npc' || actor.actorType === 'bot';
  }

  if ((actor.actorType === 'npc' || actor.actorType === 'bot') && actor.accessLevels.includes(door.requiredAccess)) {
    return true;
  }

  const cardIds = (door.accessCardObjectIds ?? []).filter(Boolean);
  if (cardIds.length > 0) {
    const inventoryIds = new Set(actor.inventoryItemIds ?? []);
    return accessMode === 'all_cards_required'
      ? cardIds.every((cardId) => inventoryIds.has(cardId))
      : cardIds.some((cardId) => inventoryIds.has(cardId));
  }

  if (accessMode === 'all_cards_required') {
    return false;
  }

  return actor.accessLevels.includes(door.requiredAccess);
}

export function doorAccessRequirementLabel(door: MysteryRoomDoorRuntimeState): string {
  switch (door.accessMode) {
    case 'public':
      return 'общественная';
    case 'npc_only':
      return 'только NPC';
    case 'all_cards_required':
      return door.accessCardObjectIds.length > 0 ? `нужны все пропуска: ${door.accessCardObjectIds.join(', ')}` : 'нужны все пропуска';
    case 'card_required':
    default:
      return door.accessCardObjectIds.length > 0 ? `нужен пропуск: ${door.accessCardObjectIds.join(' / ')}` : `нужен доступ: ${door.requiredAccess}`;
  }
}

function rectsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/** Зачем: P2P-хост — двери видят гостей (пропуски/карты из теневого инвентаря). */
export function doorActorsFromP2pGuestShadows(
  shadows: Map<string, MysteryRoomHeroState>,
  inventories: Map<string, MysteryRoomInventoryItemState[]>,
): DoorActor[] {
  const out: DoorActor[] = [];
  shadows.forEach((hero, peerId) => {
    if (!hero.alive) {
      return;
    }
    const inv = inventories.get(peerId) ?? [];
    out.push({
      actorId: peerId,
      actorType: 'player',
      bounds: {
        x: hero.position.x - hero.radius,
        y: hero.position.y - hero.radius,
        width: hero.radius * 2,
        height: hero.radius * 2,
      },
      accessLevels: hero.accessLevels,
      inventoryItemIds: inv.filter((item) => (item.quantity ?? 1) > 0).map((item) => item.id),
    });
  });
  return out;
}
